import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaUserCircle, FaBookmark, FaTags, FaCrown, FaSignOutAlt } from 'react-icons/fa';
import PaperCollection from './PaperCollection';
import SubscriptionManagement from './SubscriptionManagement';
import FavoriteTagsPanel from './FavoriteTagsPanel';
import { paperApi } from '../utils/apiUtils';

function User() {
  const { user, signOut, loading, error, fetchUser, favoritePaperIds, eligibility } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [favoritePapers, setFavoritePapers] = useState([]);

  useEffect(() => { fetchUser(); }, [fetchUser]);
  useEffect(() => { if (!loading && !user && !error) navigate('/sign-in'); }, [user, loading, error, navigate]);
  useEffect(() => { if (favoritePaperIds.length > 0) fetchFavoritePapers(); }, [favoritePaperIds]);

  const handleSignOut = async () => {
    try { await signOut(); navigate('/sign-in'); }
    catch (error) { console.error('Error signing out:', error); }
  };

  const fetchFavoritePapers = async () => {
    try {
      const papers = [];
      for (const paperId of favoritePaperIds) {
        const { paper } = await paperApi.fetchPaperById(paperId);
        papers.push(paper);
      }
      setFavoritePapers(papers);
    } catch (error) {
      console.error('Error fetching favorite papers:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center">
        <div className="w-6 h-6 border-2 border-white/20 border-t-yellow-300 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/30 text-xs">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="bg-white rounded-2xl shadow-glass p-8">
          <p className="text-red-500 text-xs">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // A user whose Stripe sync is still in-flight is implicitly on free_plan —
  // the server's sync-status endpoint will reconcile them shortly, but we
  // render the floor state meanwhile so no one ever sees "None".
  const effectiveTier = user?.stripeSubscriptionTier || 'free_plan';

  const renderSubscriptionStatus = (status) => {
    const classes = {
      free_plan: "bg-gray-100 text-gray-600",
      pro_plan: "bg-gradient-to-r from-purple-500 to-purple-600 text-white",
      premium_plan: "bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900",
    };
    const icons = { free_plan: null, pro_plan: <FaCrown className="inline mr-1 text-[9px]" />, premium_plan: <FaCrown className="inline mr-1 text-[9px]" /> };
    const label = status ? status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'None';
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${classes[status] || classes.free_plan}`}>
        {icons[status] || icons.free_plan}{label}
      </span>
    );
  };

  // Stringify a (usage, quota) pair for the Profile quota rows. Infinity
  // displays as "Unlimited"; 0 quota displays as "Not included".
  const formatQuota = (usage, quota) => {
    if (quota === 0) return 'Not included';
    if (!Number.isFinite(quota)) return `${usage} / Unlimited`;
    return `${usage} / ${quota}`;
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: FaUserCircle },
    { id: 'favorites', label: 'Favorites', icon: FaBookmark },
    { id: 'tags', label: 'Tags', icon: FaTags },
    { id: 'subscription', label: 'Plan', icon: FaCrown }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Name', value: user.name },
                { label: 'Email', value: user.email },
                { label: 'Plan', value: renderSubscriptionStatus(effectiveTier), isJsx: true },
                { label: 'Last Login', value: new Date(user.lastLogin).toLocaleString() },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-[10px] text-gray-300 uppercase tracking-widest font-semibold mb-1.5">{item.label}</p>
                  {item.isJsx ? <div>{item.value}</div> : <p className="text-sm font-medium text-gray-900">{item.value}</p>}
                </div>
              ))}
            </div>
            <div>
              <p className="text-[10px] text-gray-300 uppercase tracking-widest font-semibold mb-2">Usage this period</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: 'Saved Papers', value: formatQuota(eligibility.favoritePaperUsage, eligibility.favoritePaperQuota) },
                  { label: 'Followed Topics', value: formatQuota(eligibility.favoriteTagUsage, eligibility.favoriteTagQuota) },
                  { label: 'AI Summaries', value: formatQuota(eligibility.aiSummaryUsage, eligibility.aiSummaryQuota) },
                  { label: 'AI Chats', value: formatQuota(eligibility.aiChatUsage, eligibility.aiChatQuota) },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4">
                    <p className="text-[10px] text-gray-300 uppercase tracking-widest font-semibold mb-1.5">{item.label}</p>
                    <p className="text-sm font-medium text-gray-900 tabular-nums">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'favorites':
        return <PaperCollection papers={favoritePapers} onSave={() => {}} onRemove={() => {}} />;
      case 'tags':
        return <FavoriteTagsPanel />;
      case 'subscription':
        return <SubscriptionManagement />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-4 sm:py-8">
      {/* Profile header */}
      <div className="bg-white rounded-2xl shadow-glass overflow-hidden border border-gray-100 mb-5">
        <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400" />
        <div className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
            <FaUserCircle className="text-purple-400 text-sm" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">{user.name}</h1>
            <p className="text-xs text-gray-400 truncate">{user.email}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-glass overflow-hidden border border-gray-100">
        <div className="border-b border-gray-100">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`flex items-center px-4 py-3 text-xs font-semibold transition-all duration-200 border-b-2 ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-300 hover:text-gray-500'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon className={`mr-1.5 text-[10px] ${activeTab === tab.id ? 'text-purple-400' : 'text-gray-200'}`} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-5">{renderTabContent()}</div>
      </div>

      <motion.button
        whileTap={{ scale: 0.98 }}
        className="mt-5 flex items-center px-4 py-2 text-xs font-semibold rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
        onClick={handleSignOut}
      >
        <FaSignOutAlt className="mr-1.5 text-[10px]" />
        Sign Out
      </motion.button>
    </div>
  );
}

export default User;
